import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot
} from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { JwtPayload } from '../../core/core/model/jwt-payload.model';
import { AUTH_CONSTANTS } from '../constants/auth.constants';

/**
 * Guard que redirige a la pantalla "sin perfil" cuando el usuario autenticado
 * no tiene ningún rol/perfil asignado en el token JWT.
 *
 * @remarks
 * - Si no hay token: deja pasar (AuthTokenGuard ya cubre ese caso)
 * - Si hay token con authorities vacíos: redirige a /auth/sin-perfil
 * - Si hay token con authorities: permite navegación normal
 *
 * Aplica al layout principal (AdminLayoutComponent) para interceptar
 * cualquier acceso directo, independientemente del flujo de login.
 */
@Injectable({ providedIn: 'root' })
export class NoProfileGuard implements CanActivate {
  constructor(private readonly router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): boolean {
    const token = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEY_TOKEN);

    if (!token) {
      // Sin token: no es responsabilidad de este guard
      return true;
    }

    try {
      const payload = jwtDecode<JwtPayload>(token);

      const authorities = payload.authorities;
      const roles: string[] = Array.isArray(authorities)
        ? authorities
        : authorities
          ? [authorities]
          : [];

      if (roles.length === 0) {
        this.router.navigate([AUTH_CONSTANTS.SIN_PERFIL_URL]);
        return false;
      }

      return true;
    } catch {
      // Token malformado: dejar pasar, AuthTokenGuard lo manejará
      return true;
    }
  }
}
